import React, { useState } from 'react';
import './Form.css';

const Form = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    phone: '',
    email: '',
    service: '',
    message: '',
    agree: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Services options
  const services = [
    { value: '', label: 'Select a service...' },
    { value: 'market-entry', label: '01 - Market Entry & Incorporation' },
    { value: 'fdi-advisory', label: '02 - FDI Advisory & RBI Compliance' },
    { value: 'finance-accounting', label: '03 - Finance & Accounting' },
    { value: 'audit-assurance', label: '04 - Audit & Assurance' },
    { value: 'taxation-compliance', label: '05 - Taxation & Regulatory Compliance' },
    { value: 'hr-payroll', label: '06 - Human Resources & Payroll' },
    { value: 'legal-compliance', label: '07 - Legal & Corporate Compliance' },
    { value: 'ip-cyber-law', label: '08 - Intellectual Property & Cyber Law' }
  ];

  // Validation functions
  const validateName = (name, fieldName) => {
    if (!name.trim()) {
      return `${fieldName} is required`;
    }
    if (name.trim().length < 2) {
      return `${fieldName} must be at least 2 characters`;
    }
    if (!/^[a-zA-Z\s]+$/.test(name)) {
      return `${fieldName} should contain only letters`;
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) {
      return 'Phone number is required';
    }
    if (!/^\d{10}$/.test(phone)) {
      return 'Phone number must be exactly 10 digits';
    }
    return '';
  };

  const validateEmail = (email) => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validateGender = (gender) => {
    return gender ? '' : 'Please select a gender';
  };

  const validateService = (service) => {
    return service ? '' : 'Please select a service';
  };

  const validateAgreement = (agree) => {
    return agree ? '' : 'You must agree to the terms and conditions';
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'phone') {
      // Only allow digits for phone number
      const numericValue = value.replace(/\D/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle field blur for real-time validation
  const handleFieldBlur = (fieldName) => {
    let error = '';
    
    switch (fieldName) {
      case 'firstName':
        error = validateName(formData.firstName, 'First name');
        break;
      case 'lastName':
        error = validateName(formData.lastName, 'Last name');
        break;
      case 'phone':
        error = validatePhone(formData.phone);
        break;
      case 'email':
        error = validateEmail(formData.email);
        break;
      case 'gender':
        error = validateGender(formData.gender);
        break;
      case 'service':
        error = validateService(formData.service);
        break;
      case 'agree':
        error = validateAgreement(formData.agree);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {
      firstName: validateName(formData.firstName, 'First name'),
      lastName: validateName(formData.lastName, 'Last name'),
      phone: validatePhone(formData.phone),
      email: validateEmail(formData.email),
      gender: validateGender(formData.gender),
      service: validateService(formData.service),
      agree: validateAgreement(formData.agree)
    };

    setErrors(newErrors);

    // Check if form is valid
    const isValid = Object.values(newErrors).every(error => error === '');

    if (isValid) {
      setIsSubmitting(true);
      
      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false);
        setShowModal(true);
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          gender: '',
          phone: '',
          email: '',
          service: '',
          message: '',
          agree: false
        });
        
        // Clear errors
        setErrors({});
      }, 1500);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="form-container">
          <div className="form-header">
            <h1>Inquiry Form</h1>
          </div>
          
          <div className="form-body">
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">
                    First Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('firstName')}
                    className={errors.firstName ? 'error' : ''}
                  />
                  {errors.firstName && (
                    <div className="error-message">{errors.firstName}</div>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="lastName">
                    Last Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    onBlur={() => handleFieldBlur('lastName')}
                    className={errors.lastName ? 'error' : ''}
                  />
                  {errors.lastName && (
                    <div className="error-message">{errors.lastName}</div>
                  )}
                </div>
              </div>
              
              <div className="form-group full-width">
                <label>Gender <span className="required">*</span></label>
                <div className="gender-group">
                  <div className="gender-option">
                    <input
                      type="radio"
                      id="male"
                      name="gender"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="male">Male</label>
                  </div>
                  <div className="gender-option">
                    <input
                      type="radio"
                      id="female"
                      name="gender"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="female">Female</label>
                  </div>
                  <div className="gender-option">
                    <input
                      type="radio"
                      id="other"
                      name="gender"
                      value="other"
                      checked={formData.gender === 'other'}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="other">Other</label>
                  </div>
                </div>
                {errors.gender && (
                  <div className="error-message">{errors.gender}</div>
                )}
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="phone">
                  Phone Number <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={() => handleFieldBlur('phone')}
                  placeholder="Enter 10-digit phone number"
                  maxLength="10"
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && (
                  <div className="error-message">{errors.phone}</div>
                )}
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="email">
                  Email ID <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => handleFieldBlur('email')}
                  placeholder="Enter your email address"
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}
              </div>

              <div className="form-group full-width">
                <label htmlFor="service">
                  Services We Provide <span className="required">*</span>
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  onBlur={() => handleFieldBlur('service')}
                  className={errors.service ? 'error' : ''}
                >
                  {services.map((service) => (
                    <option key={service.value} value={service.value}>
                      {service.label}
                    </option>
                  ))}
                </select>
                {errors.service && (
                  <div className="error-message">{errors.service}</div>
                )}
              </div>
              
              <div className="form-group full-width">
                <label htmlFor="message">Message/Query</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Enter your message or query here..."
                />
              </div>
              
              <div className="checkbox-group">
                <input
                  type="checkbox"
                  id="agree"
                  name="agree"
                  checked={formData.agree}
                  onChange={handleInputChange}
                />
                <label htmlFor="agree">
                  I agree to the terms and conditions <span className="required">*</span>
                </label>
              </div>
              {errors.agree && (
                <div className="error-message">{errors.agree}</div>
              )}
              
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          </div>
        </div>
      </div>
      
      {/* Success Modal */}
      {showModal && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-icon">✓</div>
            <h2>Form Submitted Successfully!</h2>
            <p>Thank you for your inquiry. We have received your message and will get back to you soon.</p>
            <button className="modal-btn" onClick={closeModal}>Close</button>
          </div>
        </div>
      )}
    </>
  );
};

export default Form;